import { runTransaction, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Maps country ISO codes to 2-letter prefixes
 */
 const COUNTRY_CODE_MAP = {
   'ARE': 'AE', // United Arab Emirates
   'BHR': 'BH', // Bahrain
   'SAU': 'SA', // Saudi Arabia
   'PHL': 'PH', // Philippines
 };

/**
 * Gets the 2-letter country code for order numbering
 * @param {string} countryIso - 3-letter ISO country code (e.g., 'BHR', 'ARE', 'SAU')
 * @returns {string} 2-letter country code (e.g., 'BH', 'AE', 'SA', 'ZZ')
 */
const getCountryCode = (countryIso) => {
  return COUNTRY_CODE_MAP[countryIso] || 'ZZ'; // Default to ZZ for rest of world
};

/**
 * Generates a new order number using country-specific counters in Firestore
 * Format: {COUNTRY_CODE}{SEQUENCE} where:
 * - COUNTRY_CODE is 2 letters (AE, BH, SA, or ZZ)
 * - SEQUENCE is a 4-digit zero-padded number per country (0001-9999)
 * Example: AE0001, AE0002, BH0001, BH0002, SA0001, ZZ0001, etc.
 * 
 * @param {string} countryIso - 3-letter ISO country code (e.g., 'BHR', 'ARE', 'SAU')
 * @param {number} maxRetries - Maximum number of retry attempts
 * @returns {Promise<string>} Generated order number
 */
export const generateOrderNumber = async (countryIso, maxRetries = 3) => {
  const counterRef = doc(db, 'counters', 'orders');
  const countryCode = getCountryCode(countryIso);
  let attempts = 0;
  let lastError = null;

  while (attempts < maxRetries) {
    try {
      // Use a transaction to safely increment the country-specific counter
      const sequentialNumber = await runTransaction(db, async (transaction) => {
        // Get the current counter document
        const counterDoc = await transaction.get(counterRef);
        let currentCount = 1; // Start from 1 if no counter exists
        
        if (counterDoc.exists()) {
          const data = counterDoc.data();
          currentCount = (data[countryCode] || 0) + 1;
        }
        
        // Update only the specific country counter field
        transaction.set(counterRef, { 
          [countryCode]: currentCount,
          [`${countryCode}_lastUpdated`]: serverTimestamp()
        }, { merge: true });
        
        return currentCount;
      });
      
      // If we get here, the transaction was successful
      // Format sequence as 4-digit zero-padded number
      const formattedSequence = sequentialNumber.toString().padStart(4, '0');
      // Return country code + padded sequence (e.g., AE0001, BH0002, SA0003, ZZ0001)
      return `${countryCode}${formattedSequence}`;
      
    } catch (error) {
      lastError = error;
      attempts++;
      
      if (attempts < maxRetries) {
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempts) * 100));
      }
    }
  }
  
  // If we get here, all retries failed
  console.error('Failed to generate order number after', maxRetries, 'attempts:', lastError);
  
  // Fallback to timestamp-based number with country code
  const timestamp = Date.now().toString().slice(-6);
  return `${countryCode}F${timestamp}`;
};

/**
 * Generates a draft order number in the format: CCC-AGT-YYYYMMDD-HHMM
 * - CCC: 3-letter country ISO code
 * - AGT: 3-letter uppercase agent code
 * - YYYYMMDD-HHMM: creation timestamp
 */
export const generateDraftOrderNumber = (countryIso, agentCode = '') => {
  const normalize = (value, length) => {
    const clean = String(value || '').toLowerCase().replace(/[^a-z]/g, '');
    return clean.substring(0, length).padEnd(length, 'x');
  };

  const country = normalize(countryIso, 3);
  const agent = normalize(agentCode, 3);
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const timestamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate())
  ].join('') + '_' + [pad(now.getHours()), pad(now.getMinutes())].join('');

  return `${country}_${agent}_${timestamp}`;
};

/**
 * Creates a unique document ID for orders
 * Format: {ORDERID}_TimestampYYYYMMDDHHMMSSsss{4chars_random}
 * Example: A07808_202508231357123guok
 */
export const generateOrderDocId = (orderNumber) => {
  // Generate timestamp in YYYYMMDDHHMMSS format
  const now = new Date();
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  const timestamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds())
  ].join('');

  // Generate 4 random alphanumeric characters (lowercase)
  const randomSuffix = Math.random().toString(36).substring(2, 6);
  
  // Compose document ID as ORDERID_TimestampYYYYMMDDHHMMSSsss{4chars_random}
  return `${orderNumber}_${timestamp}${pad(now.getMilliseconds(), 3)}${randomSuffix}`;
};

/**
 * Transforms raw payment period keys (e.g. 'day30') into user-friendly labels based on guidelines.
 * @param {string} period - Raw period key
 * @returns {string} User-friendly label
 */
export const transformPaymentPeriodLabel = (period) => {
  const labels = {
    'day1': 'Pay full within 2 days',
    'day30': 'Pay full by end of the month',
    'day60': 'Pay full within 2 months',
    'day90': 'Pay full within 3 months',
    'day180': 'Pay full within 6 months'
  };
  return labels[period] || period;
};

